import React, { useState, useEffect } from 'react';
import dashboardApi from '../services/dashboard';
import { KPI_META, KPI_ORDER, kpiState, kpiDisplayValue, SERVICE_BREAKDOWN, SERVICE_BREAKDOWN_KPIS, serviceBreakdownState, KPI_GROUPS, DYNAMIC_BREAKDOWN_KPIS, dynamicBreakdownState, DYNAMIC_BREAKDOWN_SHOW_TOTAL } from '../utils/dashboardKpis';
import './DashboardHome.css';

// Tableau de bord = cockpit d'action. Une carte par indicateur global « reste à
// faire », un seul chiffre clé, un état visuel (à jour / à surveiller / urgent),
// cliquable vers la page concernée. Aucun détail nominatif : agrégats uniquement.

// Ligne dédiée à un KPI ventilé par service (5 cartes VN/VO/Atelier/Pièces/Autres)
// au lieu d'une carte agrégée unique — mêmes cartes `.dh-card` que la grille
// générique (même gabarit rectangulaire), rangées 5 par ligne.
function ServiceBreakdownRow({ kpi, onNavigate }) {
  const parService = kpi.extra?.par_service || {};
  return (
    <div className="dh-services-block">
      <button type="button" className="dh-services-head" onClick={() => onNavigate(kpi.meta.nav)}>
        <span className="dh-icon" aria-hidden="true">{kpi.meta.icon}</span>
        <span className="dh-services-title">{kpi.meta.label}</span>
        <span className="dh-go dh-services-go" aria-hidden="true">→</span>
      </button>
      <div className="dh-services-row">
        {SERVICE_BREAKDOWN.map((s) => {
          const value = parService[s.key] ?? 0;
          const state = serviceBreakdownState(kpi.key, value);
          return (
            <button
              key={s.key}
              type="button"
              className={`dh-card dh-state-${state}`}
              onClick={() => onNavigate(kpi.meta.nav)}
            >
              <div className="dh-card-top">
                <span className="dh-dot" aria-hidden="true" />
              </div>
              <div className="dh-value">{value.toLocaleString('fr-FR')}</div>
              <div className="dh-label">{s.label}</div>
              <span className="dh-go" aria-hidden="true">→</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Ligne dédiée à un KPI ventilé par une liste dynamique (ex. marque) plutôt qu'un
// catalogue fixe de 5 catégories — distinct de ServiceBreakdownRow (VN/VO/Atelier/
// Pièces/Autres, connu à l'avance). Trié par valeur décroissante.
function DynamicBreakdownRow({ kpi, extraKey, onNavigate, showTotal }) {
  const parCategorie = kpi.extra?.[extraKey] || {};
  const entries = Object.entries(parCategorie).sort((a, b) => b[1] - a[1]);
  return (
    <div className="dh-services-block">
      <button
        type="button"
        className={`dh-services-head${showTotal ? ' dh-services-head-full' : ''}`}
        onClick={() => onNavigate(kpi.meta.nav)}
      >
        <span className="dh-icon" aria-hidden="true">{kpi.meta.icon}</span>
        <span className="dh-services-title">{kpi.meta.label}</span>
        {showTotal && <span className="dh-services-total">{Number(kpi.value).toLocaleString('fr-FR')}</span>}
        <span className="dh-go dh-services-go" aria-hidden="true">→</span>
      </button>
      <div className="dh-group-row">
        {entries.map(([label, value]) => {
          const state = dynamicBreakdownState(kpi.key, value);
          return (
            <button
              key={label}
              type="button"
              className={`dh-card dh-state-${state}`}
              onClick={() => onNavigate(kpi.meta.nav)}
            >
              <div className="dh-card-top">
                <span className="dh-dot" aria-hidden="true" />
              </div>
              <div className="dh-value">{value.toLocaleString('fr-FR')}</div>
              <div className="dh-label">{label}</div>
              <span className="dh-go" aria-hidden="true">→</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Bloc titré regroupant plusieurs KPI indépendants (chacun garde son propre
// libellé/nav/seuils) — distinct de ServiceBreakdownRow (ventilation d'un seul
// KPI par service). Mêmes cartes `.dh-card` compactées (cf. `.dh-group-row`).
function KpiGroupBlock({ group, kpis, onNavigate }) {
  return (
    <div className="dh-services-block">
      <div className="dh-services-head-static">
        <span className="dh-icon" aria-hidden="true">{group.icon}</span>
        <span className="dh-services-title">{group.label}</span>
      </div>
      <div className="dh-group-row">
        {kpis.map(({ key, meta, value }) => {
          const state = kpiState(meta, value);
          const display = kpiDisplayValue(meta, value);
          return (
            <button
              key={key}
              type="button"
              className={`dh-card dh-state-${state}`}
              onClick={() => onNavigate(meta.nav)}
            >
              <div className="dh-card-top">
                <span className="dh-dot" aria-hidden="true" />
              </div>
              <div className="dh-value">{display}</div>
              <div className="dh-label">{meta.label}</div>
              <span className="dh-go" aria-hidden="true">→</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DashboardHome({ user, onNavigate, concessions = [] }) {
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedConcession, setSelectedConcession] = useState('');
  const isSuperAdmin = user.role === 'super-admin';

  useEffect(() => { load(); }, [selectedConcession]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const params = isSuperAdmin && selectedConcession ? { concession_id: selectedConcession } : undefined;
      const r = await dashboardApi.getKpis(params);
      // Indexe la réponse puis ré-ordonne selon KPI_ORDER (les absents sont ignorés).
      const byKey = Object.fromEntries((r.data.kpis || []).map((k) => [k.key, k]));
      const ordered = KPI_ORDER
        .filter((key) => byKey[key] && KPI_META[key])
        .map((key) => ({ key, meta: KPI_META[key], ...byKey[key] }));
      setKpis(ordered);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const heure = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long',
  });

  const byKey = Object.fromEntries(kpis.map((k) => [k.key, k]));
  const groups = KPI_GROUPS
    .map((g) => ({ group: g, kpis: g.members.map((k) => byKey[k]).filter(Boolean) }))
    .filter((g) => g.kpis.length > 0);
  const groupedKeys = new Set(groups.flatMap((g) => g.kpis.map((k) => k.key)));

  const breakdownKpis = kpis.filter((k) => SERVICE_BREAKDOWN_KPIS.includes(k.key));
  const dynamicKpis = kpis.filter((k) => DYNAMIC_BREAKDOWN_KPIS[k.key]);
  const otherKpis = kpis.filter((k) =>
    !SERVICE_BREAKDOWN_KPIS.includes(k.key) && !groupedKeys.has(k.key) && !DYNAMIC_BREAKDOWN_KPIS[k.key]
  );

  return (
    <div className="page-container dashboard-home">
      <div className="dh-head">
        <div className="dh-head-text">
          <h2 className="dh-title">Ce qu'il reste à traiter</h2>
          <p className="dh-subtitle">{heure.charAt(0).toUpperCase() + heure.slice(1)}</p>
        </div>
        {isSuperAdmin && concessions.length > 0 && (
          <select
            className="page-select"
            value={selectedConcession}
            onChange={(e) => setSelectedConcession(e.target.value)}
          >
            <option value="">Toutes les concessions</option>
            {concessions.map((c) => (
              <option key={c.id} value={c.id}>{c.nom}</option>
            ))}
          </select>
        )}
      </div>

      {loading && (
        <div className="dh-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="dh-card dh-skeleton" aria-hidden="true" />
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="dh-empty">
          Impossible de charger les indicateurs.{' '}
          <button className="dh-retry" onClick={load}>Réessayer</button>
        </div>
      )}

      {!loading && !error && kpis.length === 0 && (
        <div className="dh-empty">Aucun indicateur disponible pour votre profil.</div>
      )}

      {!loading && !error && kpis.length > 0 && (<>
        {breakdownKpis.length > 0 && (
          <div className="dh-services-wrap">
            {breakdownKpis.map((kpi) => (
              <ServiceBreakdownRow key={kpi.key} kpi={kpi} onNavigate={onNavigate} />
            ))}
          </div>
        )}

        {groups.length > 0 && (
          <div className="dh-services-wrap">
            {groups.map(({ group, kpis: groupKpis }) => (
              <KpiGroupBlock key={group.key} group={group} kpis={groupKpis} onNavigate={onNavigate} />
            ))}
          </div>
        )}

        {dynamicKpis.length > 0 && (
          <div className="dh-services-wrap">
            {dynamicKpis.map((kpi) => (
              <DynamicBreakdownRow
                key={kpi.key}
                kpi={kpi}
                extraKey={DYNAMIC_BREAKDOWN_KPIS[kpi.key]}
                onNavigate={onNavigate}
                showTotal={DYNAMIC_BREAKDOWN_SHOW_TOTAL.includes(kpi.key)}
              />
            ))}
          </div>
        )}

        {otherKpis.length > 0 && (
          <div className="dh-grid">
            {otherKpis.map(({ key, meta, value, extra }) => {
              const state = kpiState(meta, value);
              const display = kpiDisplayValue(meta, value);
              const isToDo = meta.kind === 'count';
              return (
                <button
                  key={key}
                  type="button"
                  className={`dh-card dh-state-${state}`}
                  onClick={() => onNavigate(meta.nav)}
                >
                  <div className="dh-card-top">
                    <span className="dh-icon" aria-hidden="true">{meta.icon}</span>
                    <span className="dh-dot" aria-hidden="true" />
                  </div>
                  <div className="dh-value">{display}</div>
                  <div className="dh-label">{meta.label}</div>
                  <div className="dh-foot">
                    {isToDo && value <= 0
                      ? <span className="dh-foot-ok">✓ {meta.zero || 'À jour'}</span>
                      : meta.kind === 'rate' && extra
                        ? <span>{Number(extra.answered).toLocaleString('fr-FR')} / {Number(extra.total).toLocaleString('fr-FR')} appels pris</span>
                        : <span>{meta.hint}</span>}
                  </div>
                  <span className="dh-go" aria-hidden="true">→</span>
                </button>
              );
            })}
          </div>
        )}
      </>)}
    </div>
  );
}

export default DashboardHome;
